import React from 'react';
import { Box, Paper, Typography, useTheme, useMediaQuery, IconButton, Tooltip } from '../ui';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { UseFormReturn } from 'react-hook-form';
import { VisibilityNode, evaluateVisibilityRule } from '../engine/runtime/visibility/dsl';
import QuickCheck from './QuickCheck';
import { getConfig } from '@config/env';
import { normalizeBudgetFrequency, toMonthlyBudgetAmount } from '@/shared/lib/budgetFrequency';
// Removed schema prompt system; warnings are now driven solely by the centralized engine
// Red Warning Engine (data-driven)
import type { OpenActionSpec, ResolvedWarning } from '@/shared/warnings/types';
import { useFieldWarnings } from '@/shared/warnings/useFieldWarnings';
import { useWarningsRefreshKey } from '@/shared/warnings/WarningsRefreshContext';
import FieldWarning from '@/components/warnings/FieldWarning';
// NI transformation for council tax → council rates
import { replaceCouncilTaxWithRates } from '@/shared/lib/niSchemaTransformer';

interface TooltipConfig {
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

interface ClickableSegment {
  text: string;
  action: 'openHelpDialog' | 'openNotesDialog' | 'focusField' | 'openSchemaDialog';
  targetFieldName?: string;
  schemaKey?: string;
}

interface QuickCheckConfig {
  visibilityCondition: VisibilityNode;
  message: string;
  clickableSegments: ClickableSegment[];
}

interface QuestionGroupProps {
  label: string;
  helpText?: string;
  children: React.ReactNode;
  mainTooltip?: TooltipConfig;
  showCalculatedIncome?: boolean;
  fieldName?: string;
  form?: UseFormReturn<any>;
  // Direct value props for use without react-hook-form
  amountValue?: number | string | null;
  frequencyValue?: string | null;
  onHelpClick?: () => void;
  onCalculatedIncomeHelpClick?: () => void;
  quickCheck?: QuickCheckConfig;
  onQuickCheckHelpOpen?: (() => void) | undefined;
  onQuickCheckNotesOpen?: (() => void) | undefined;
  onQuickCheckFieldFocus?: ((fieldName: string) => void) | undefined;
  onQuickCheckOpenSchema?: ((schemaKey: string) => void) | undefined;
  categoryKey?: string;
  onOpenFieldEdit?: ((schemaKey: string, fieldName: string, apiFieldName: string) => void) | undefined;
  onWarningAction?: ((action: OpenActionSpec, metadata?: { varId?: string }) => void) | undefined;
  error?: boolean;
  errorMessage?: string | undefined;
  // Appearance controls
  hideContainerBorder?: boolean;
  disableCalculatedOverlap?: boolean;
  // When true, renders as Box instead of Paper (useful for dialogs)
  noPaper?: boolean;
  /** When true, transforms "council tax" to "council rates" in Quick check warnings */
  isNorthernIreland?: boolean;
}

const QuestionGroup: React.FC<QuestionGroupProps> = ({
  label,
  helpText,
  children,
  mainTooltip,
  showCalculatedIncome = false,
  fieldName,
  form,
  amountValue: directAmountValue,
  frequencyValue: directFrequencyValue,
  onHelpClick,
  onCalculatedIncomeHelpClick,
  quickCheck,
  onQuickCheckHelpOpen,
  onQuickCheckNotesOpen,
  onQuickCheckFieldFocus,
  onQuickCheckOpenSchema,
  categoryKey,
  onOpenFieldEdit,
  onWarningAction,
  error = false,
  errorMessage,
  hideContainerBorder = false,
  disableCalculatedOverlap = false,
  noPaper = false,
  isNorthernIreland = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const warningsRefreshKey = useWarningsRefreshKey();

  // SpecialScreen can render duplicate inputs and react-hook-form can lag behind what
  // the user is currently typing. For warning calculations we prefer the ACTIVE DOM
  // values so cross-field prompts (e.g. HB vs Rent) update live while editing.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [domOverrides, setDomOverrides] = React.useState<{
    amount: string | null;
    frequency: string | null;
  }>({ amount: null, frequency: null });
  const domOverridesRef = React.useRef(domOverrides);
  const [localWarningRefreshKey, setLocalWarningRefreshKey] = React.useState(0);

  React.useEffect(() => {
    domOverridesRef.current = domOverrides;
  }, [domOverrides]);

  React.useEffect(() => {
    if (!fieldName) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const pickBestInput = (
      root: ParentNode,
      selector: string,
      expectedName?: string
    ): HTMLInputElement | null => {
      const nodes = Array.from(root.querySelectorAll(selector)) as HTMLInputElement[];
      if (nodes.length === 0) return null;

      // Prefer the actively focused input inside this card
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement &&
        root instanceof HTMLElement &&
        root.contains(active) &&
        (!expectedName || active.name === expectedName)
      ) {
        return active;
      }

      // Prefer visible enabled inputs (SpecialScreen can render duplicates, including hidden/stale ones)
      const visible = nodes.filter((n) => {
        if (!n) return false;
        if (n.disabled) return false;
        // offsetParent is null for display:none; also exclude hidden type
        if ((n as any).type === 'hidden') return false;
        const el = n as any;
        return el.offsetParent !== null;
      });
      if (visible.length > 0) return visible[visible.length - 1] ?? null;

      return nodes[nodes.length - 1] ?? null;
    };

    const readDom = (): { amount: string | null; frequency: string | null } => {
      const safeField = String(fieldName);
      const rootNode: ParentNode = rootRef.current ?? document;
      const rootEl = rootRef.current;

      // Prefer the actively focused input inside this card, regardless of which selector branch we hit.
      // SpecialScreen can render duplicate inputs; the focused one is the source of truth while typing.
      const active = document.activeElement;
      let amountEl: HTMLInputElement | null = null;
      if (
        active instanceof HTMLInputElement &&
        rootEl instanceof HTMLElement &&
        rootEl.contains(active)
      ) {
        const activeName = String(active.getAttribute('name') || active.name || '');
        const looksLikeAmount =
          activeName === `${safeField}.amount` ||
          activeName === safeField ||
          activeName === 'amount' ||
          activeName.endsWith('.amount');
        if (looksLikeAmount) {
          amountEl = active;
        }
      }

      const fallbackAmountEl =
        pickBestInput(rootNode, `input[data-field-path="${safeField}.amount"]`, `${safeField}.amount`) ||
        pickBestInput(rootNode, `input[name="${safeField}.amount"]`, `${safeField}.amount`) ||
        pickBestInput(rootNode, `input[name="${safeField}"]`, safeField) ||
        pickBestInput(rootNode, `input[name="amount"]`, 'amount');
      if (!amountEl) {
        amountEl = fallbackAmountEl;
      }
      const amount = amountEl ? String(amountEl.value ?? '') : null;

      const freqInputs = Array.from(
        rootNode.querySelectorAll(`[name="${safeField}.frequency"]`)
      ) as Array<HTMLInputElement | HTMLSelectElement>;
      let freqEl =
        freqInputs.length > 0 ? freqInputs[freqInputs.length - 1] : null;
      if (!freqEl) {
        const fallbackFreqInputs = Array.from(
          rootNode.querySelectorAll(`[name="frequency"]`)
        ) as Array<HTMLInputElement | HTMLSelectElement>;
        freqEl =
          fallbackFreqInputs.length > 0
            ? fallbackFreqInputs[fallbackFreqInputs.length - 1]
            : null;
      }
      const frequency = freqEl ? String((freqEl as any).value ?? '') : null;

      return { amount, frequency };
    };

    const updateFromDom = () => {
      const next = readDom();
      const prev = domOverridesRef.current;
      const changed =
        next.amount !== prev.amount || next.frequency !== prev.frequency;
      if (!changed) return;
      domOverridesRef.current = next;
      setDomOverrides(next);
      setLocalWarningRefreshKey((k) => k + 1);
    };

    updateFromDom();

    const handler = (e: Event) => {
      const rootEl = rootRef.current;
      const targetNode = e.target as unknown;
      if (!rootEl || !(rootEl instanceof HTMLElement)) return;
      if (!(targetNode instanceof Node) || !rootEl.contains(targetNode)) return;

      const safeField = String(fieldName);

      // Prefer taking values directly from the event target (most reliable with duplicate inputs).
      const target = targetNode as any;
      const nameAttr =
        typeof target?.getAttribute === 'function'
          ? String(target.getAttribute('name') ?? '')
          : String(target?.name ?? '');
      const fieldPathAttr =
        typeof target?.getAttribute === 'function'
          ? String(target.getAttribute('data-field-path') ?? '')
          : '';

      const isAmountTarget =
        target instanceof HTMLInputElement &&
        (fieldPathAttr === `${safeField}.amount` ||
          nameAttr === `${safeField}.amount` ||
          nameAttr === safeField ||
          nameAttr === 'amount' ||
          nameAttr.endsWith('.amount'));

      const isFrequencyTarget =
        (target instanceof HTMLInputElement ||
          target instanceof HTMLSelectElement) &&
        (fieldPathAttr === `${safeField}.frequency` ||
          nameAttr === `${safeField}.frequency` ||
          nameAttr === 'frequency' ||
          nameAttr.endsWith('.frequency'));

      if (isAmountTarget || isFrequencyTarget) {
        const prev = domOverridesRef.current;
        const next = {
          amount: isAmountTarget ? String((target as HTMLInputElement).value ?? '') : prev.amount,
          frequency: isFrequencyTarget
            ? String((target as HTMLInputElement | HTMLSelectElement).value ?? '')
            : prev.frequency,
        };
        const changed = next.amount !== prev.amount || next.frequency !== prev.frequency;
        if (changed) {
          domOverridesRef.current = next;
          setDomOverrides(next);
          setLocalWarningRefreshKey((k) => k + 1);
        }
        return;
      }

      // Fallback: still attempt a DOM scan for other cases (e.g. frequency toggles).
      updateFromDom();
    };

    window.addEventListener('input', handler, true);
    window.addEventListener('change', handler, true);
    return () => {
      window.removeEventListener('input', handler, true);
      window.removeEventListener('change', handler, true);
    };
  }, [fieldName]);

  // Interpolate placeholders like {balanceAmount} with values from the current form
  const interpolatePlaceholders = (text: string, values: Record<string, unknown>): string => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/\{(\w+)\}/g, (_match, key) => {
      const rawVal = values?.[key];
      if (rawVal === undefined || rawVal === null) return `{${key}}`;
      // Format numbers to two decimals with thousands separators; leave others as-is
      if (typeof rawVal === 'number') {
        return rawVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (typeof rawVal === 'string') {
        const asNum = Number(rawVal.replace?.(/,/g, '') ?? rawVal);
        if (!Number.isNaN(asNum)) {
          return asNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        return rawVal;
      }
      return String(rawVal);
    });
  };

  // Source of truth: ALWAYS prefer form values when form is present
  // This ensures cleared form fields don't get overridden by lingering API values
  let amountValue: unknown = undefined;
  let frequencyValue: unknown = undefined;

  if (form && fieldName) {
    const group = (form.getValues ? form.getValues(fieldName) : undefined) as Record<string, unknown> | undefined;
    amountValue = group?.amount ?? '';
    frequencyValue = (group?.frequency as string | undefined) ?? '';
  } else {
    // Only use directAmountValue when no form is bound (read-only display scenarios)
    amountValue = directAmountValue;
    frequencyValue = directFrequencyValue;
  }

  // Evaluate Quick check visibility (gated by feature flag)
  const allFormValues = form?.watch() ?? {};
  const features = React.useMemo(() => getConfig().features || {}, []);
  const shouldShowQuickCheck = (features.useSchemaPrompts !== false) && quickCheck
    ? evaluateVisibilityRule(quickCheck.visibilityCondition, allFormValues)
    : false;

  // Schema prompt system removed; centralized engine handles warnings

  // Parse helpText to add bold formatting
  const renderHelpText = (text: string) => {
    if (!text) return null;
    const allValues = (form?.watch?.() as Record<string, unknown>) || {};

    // Special handling: Bold only these placeholders when present in text
    const tokenRegex = /(£\{balanceAmount\}|£\{totalOutgoings\})/g;
    if (tokenRegex.test(text)) {
      const parts = text.split(tokenRegex);
      const nodes: React.ReactNode[] = [];
      parts.forEach((part, idx) => {
        if (part === '£{balanceAmount}') {
          const v = allValues?.balanceAmount as string | number | undefined;
          const display = typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (v ?? '');
          nodes.push(
            <Box key={`b-balance-${idx}`} component="span" sx={{ fontWeight: 700 }}>£{display}</Box>
          );
        } else if (part === '£{totalOutgoings}') {
          const v = allValues?.totalOutgoings as string | number | undefined;
          const display = typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (v ?? '');
          nodes.push(
            <Box key={`b-outgoings-${idx}`} component="span" sx={{ fontWeight: 700 }}>£{display}</Box>
          );
        } else if (part) {
          // Interpolate any other placeholders in the surrounding text
          nodes.push(<React.Fragment key={`t-${idx}`}>{interpolatePlaceholders(part, allValues) as string}</React.Fragment>);
        }
      });
      return <>{nodes}</>;
    }

    // Fallback: interpolate and attempt legacy emphasis pattern
    const interpolated = interpolatePlaceholders(text, allValues);
    const boldPattern = /provide ([^.]+income[^.]*)/i;
    const match = interpolated.match(boldPattern);
    if (match) {
      const parts = interpolated.split(match[0]);
      return (
        <>
          {parts[0]}provide{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {match[1]}
          </Box>
          {parts[1]}
        </>
      );
    }
    return interpolated;
  };

  // Parse amount value (remove commas and convert to number)
  let parsedAmount: number | undefined = undefined;
  const amountForCalc =
    domOverrides.amount !== null ? domOverrides.amount : amountValue;
  if (typeof amountForCalc === 'string') {
    // Strip any currency symbols/spaces while allowing digits, decimal, minus.
    const cleaned = amountForCalc.replace(/[^0-9.-]+/g, '');
    const parsed = parseFloat(cleaned);
    parsedAmount = isNaN(parsed) ? undefined : parsed;
  } else if (typeof amountForCalc === 'number') {
    parsedAmount = amountForCalc;
  }

  // Frequency: prefer form state (updates reliably on chip click); fall back to DOM only if form value is blank.
  const rawFrequencyFromForm = frequencyValue;
  const rawFrequencyFromDom = domOverrides.frequency;
  const rawFrequencyForCalc =
    typeof rawFrequencyFromForm === 'string' && rawFrequencyFromForm.trim().length > 0
      ? rawFrequencyFromForm
      : typeof rawFrequencyFromDom === 'string' && rawFrequencyFromDom.trim().length > 0
        ? rawFrequencyFromDom
        : rawFrequencyFromForm;

  const normalizedFrequency = normalizeBudgetFrequency(rawFrequencyForCalc);
  const hasValidData =
    typeof parsedAmount === 'number' &&
    Number.isFinite(parsedAmount) &&
    normalizedFrequency !== undefined;

  const isMonthlyFrequency = normalizedFrequency === 'monthly';

  const monthlyIncome = hasValidData
    ? toMonthlyBudgetAmount(parsedAmount as number, normalizedFrequency)
    : 0;

  const effectiveAmountValue = amountForCalc;
  const effectiveFrequencyValue = rawFrequencyForCalc;

  const formValuesForWarnings = React.useMemo(() => {
    const base = { ...(allFormValues || {}) } as Record<string, any>;
    if (!fieldName) return base;

    const existing = base[fieldName];
    const group =
      existing && typeof existing === 'object' && !Array.isArray(existing)
        ? { ...(existing as Record<string, any>) }
        : {};

    if (effectiveAmountValue !== undefined) group.amount = effectiveAmountValue;
    if (effectiveFrequencyValue !== undefined)
      group.frequency = effectiveFrequencyValue;

    base[fieldName] = group;
    // Also set flattened dot-keys because the warning engine's getValueAtPath
    // prefers exact matches like "houseRent.amount" when present.
    // SpecialScreen can leave stale dot-keys around, so we overwrite them here.
    if (effectiveAmountValue !== undefined) {
      base[`${fieldName}.amount`] = effectiveAmountValue;
    }
    if (effectiveFrequencyValue !== undefined) {
      base[`${fieldName}.frequency`] = effectiveFrequencyValue;
    }
    return base;
  }, [allFormValues, fieldName, effectiveAmountValue, effectiveFrequencyValue]);

  const engineWarnings: ResolvedWarning[] = useFieldWarnings({
    fieldName: fieldName ?? undefined,
    categoryKey: categoryKey ?? undefined,
    formValues: formValuesForWarnings,
    max: 50, // Allow multiple warnings so limit-exceeded warnings aren't lost
    refreshKey: (warningsRefreshKey ?? 0) + localWarningRefreshKey,
  });

  const content = (
    <>
      {/* Question Header */}
      <Box sx={{ mb: hideContainerBorder ? 0 : 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: isMobile ? '18px' : '20px',
              lineHeight: isMobile ? '24px' : '28px',
              flex: 1,
            }}
          >
            {label}
          </Typography>
          {(onHelpClick || mainTooltip) && (
            <Tooltip
              title={onHelpClick ? "Help & FAQs" : (mainTooltip?.content || "")}
              placement={mainTooltip?.position || 'top'}
              arrow
            >
              <IconButton
                size="small"
                onClick={onHelpClick}
                sx={{
                  width: 24,
                  height: 24,
                  color: onHelpClick ? "#9CA3AF" : theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {helpText && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: isMobile ? '14px' : '16px',
              lineHeight: isMobile ? '20px' : '22px',
              whiteSpace: 'pre-line',
            }}
          >
            {renderHelpText(helpText)}
          </Typography>
        )}
      </Box>

      {/* Children Content with Calculated Income Injected */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2.25 : 2.5 }}>
        {React.Children.map(children, (child, index) => {
          // Always use a stable wrapper around the first child to avoid remounts on toggle
          if (index === 0) {
            const showCalc = showCalculatedIncome && hasValidData && monthlyIncome > 0 && !isMonthlyFrequency;
            return (
              <Box key={`${fieldName || 'qg'}-amount-wrapper`}>
                {/* Wrap child to control spacing; remove child's margin so calculated amount sticks */}
                <Box sx={{ '& > *': { mb: '0 !important' } }}>
                  {child}
                </Box>
                {showCalc && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: disableCalculatedOverlap ? 1.25 : 1.875,
                      py: disableCalculatedOverlap ? 0.75 : 1,
                      backgroundColor: theme.palette.grey[50],
                      border: `1px solid ${theme.palette.divider}`,
                      borderTop: disableCalculatedOverlap ? `1px solid ${theme.palette.divider}` : 'none',
                      borderRadius: disableCalculatedOverlap ? '8px' : '0 0 8px 8px',
                      mt: disableCalculatedOverlap ? 0.5 : '-6px',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: isMobile ? '12px' : '14px',
                        lineHeight: isMobile ? '16px' : '20px',
                      }}
                    >
                      Calculated monthly amount: £{monthlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    {onCalculatedIncomeHelpClick && (
                      <Tooltip
                        title="Help & FAQs"
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={onCalculatedIncomeHelpClick}
                          sx={{
                            width: 18,
                            height: 18,
                            padding: 0,
                            color: "#9CA3AF",
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <HelpOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            );
          }

          // Non-first children render as-is to keep behavior unchanged
          return <React.Fragment key={index}>{child}</React.Fragment>;
        })}
      </Box>

      {/* Error message - matching TextField.tsx error handling pattern */}
      {(error || errorMessage) && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mt: 2,
            fontSize: '0.875rem',
            fontWeight: 500,
            display: 'block',
          }}
        >
          {errorMessage || 'This field is required'}
        </Typography>
      )}

      {/* Quick check - Legacy support */}
      {shouldShowQuickCheck && quickCheck && (
        <QuickCheck
          message={isNorthernIreland ? replaceCouncilTaxWithRates(quickCheck.message) : quickCheck.message}
          segments={isNorthernIreland 
            ? quickCheck.clickableSegments.map(seg => ({
                ...seg,
                text: replaceCouncilTaxWithRates(seg.text),
              }))
            : quickCheck.clickableSegments}
          onHelpDialogOpen={onQuickCheckHelpOpen}
          onNotesDialogOpen={onQuickCheckNotesOpen}
          onFieldFocus={onQuickCheckFieldFocus}
          onOpenSchemaDialog={onQuickCheckOpenSchema}
        />
      )}

      {/* Schema prompt system removed */}

      {/* Engine-driven Warning (data-driven rules) */}
      {engineWarnings.map((w) => (
        <FieldWarning
          key={w.id}
          warning={w}
          onOpenFieldEdit={onOpenFieldEdit}
          onNotesDialogOpen={onQuickCheckNotesOpen}
          onWarningAction={onWarningAction}
          isNorthernIreland={isNorthernIreland}
        />
      ))}
    </>
  );

  if (noPaper) {
    return (
      <Box
        ref={rootRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 2.25 : 2.5
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Paper
      ref={rootRef}
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 2.25 : 2.5,
        p: hideContainerBorder ? 0 : isMobile ? 1.5 : 2.75,
        borderRadius: hideContainerBorder ? 0 : 1.5,
        border: hideContainerBorder ? 'none' : `1px solid ${theme.palette.divider}`,
        backgroundColor: hideContainerBorder ? 'transparent' : 'white',
      }}
    >
      {content}
    </Paper>
  );
};

export default QuestionGroup;
